import React, { FC, useEffect, useState } from 'react'
import {
  JingleModel,
  selectAllJingles,
  selectJingleById,
} from '@store/slices/jingle.slice'
import { Button, Card, Collapse, InputGroup } from 'react-bootstrap'
import {
  fetchAllNewss,
  selectAllNews,
  selectNewsById,
} from '@store/slices/news.slice'
import ReactSelect from 'react-select'
import {
  BsArrowDown,
  BsArrowUp,
  BsEyeFill,
  BsEyeSlashFill,
  BsPencil,
  BsX,
} from 'react-icons/bs'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import NewsEditorComponent from '@components/news/newsEditor/newsEditor.component'
import { useTheme } from '@config/theme.config'
import { reactSelectDarkmode } from '@config/slate.config'
import NewsViewComponent from '@components/news/newsView.component'
import {
  getNewsStateTranslation,
  getStateColor,
} from '../../tools/newsState.tools'
import {
  deleteTimeslot, fetchTimeslotById,
  selectSlotById, TimeslotModel,
  updateTimeSlot,
} from '@store/slices/timeslot.slice'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import AudioPlayButtonComponent from '@components/shared/audioPlayButtonComponent'
import { selectStationById } from '@store/slices/station.slice'
import { moveSlot } from '@store/thunk/slot.thunk'

export interface NewsViewProps {
  slotId: EntityId
  stationId: EntityId
  numberOfSlots: number
  areaId: EntityId
  neighbourSlots: TimeslotModel[]
}

const SlotComponent: FC<NewsViewProps> = ({
  slotId,
  numberOfSlots,
  stationId,
  areaId,
  neighbourSlots,
}) => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const slot = useSelector((state: RootState) => selectSlotById(state, slotId))
  const station = useSelector((state: RootState) =>
    selectStationById(state, stationId)
  )
  const newss = useSelector(selectAllNews)
  const jingles: JingleModel[] = useSelector(selectAllJingles)
  const slotJingle = useSelector((state: RootState) =>
    slot.jingleId ? selectJingleById(state, slot?.jingleId) : null
  )
  const news = useSelector((state: RootState) =>
    slot.newsId ? selectNewsById(state, slot.newsId) : null
  )
  const [open, setOpen] = useState(true)

  const isDefaultArea = () => {
    return station.radioPreset.defaultAreaId == areaId
  }

  const [showModal, setShowModal] = useState(false)

  const onEditNews = () => {
    setShowModal(true)
  }

  const onHideEditor = (promise: Promise<EntityId> | undefined) => {
    setShowModal(false)
    if (promise)
      promise.then((newsId) => {
        if (newsId && newsId != slot.newsId) onSelectNews(newsId)
      })
  }

  const onRemoveNews = () => {
    dispatch(
      updateTimeSlot({
        id: slot.id,
        data: {
          newsId: '',
          position: slot.position,
          broadcastId: slot.broadcastId,
        },
      })
    )
  }

  const setSelectedJingleId = (newJingleId: EntityId | null) => {
    dispatch(
      updateTimeSlot({
        id: slot.id,
        data: {
          ...slot,
          jingleId: newJingleId,
        },
      })
    )
  }

  const onAddNews = () => {
    setShowModal(true)
  }

  const onSelectNews = (newsId: EntityId | null) => {
    if (newsId)
      dispatch(
        updateTimeSlot({
          id: slot.id,
          data: {
            newsId: newsId,
            position: slot.position,
            broadcastId: slot.broadcastId,
          },
        })
      )
  }

  const onRemoveSlot = (e: React.MouseEvent) => {
    e.preventDefault()
    dispatch(deleteTimeslot(slot.id))
        .then(() => {
          neighbourSlots.forEach(neighbourSlot => {
            if (slot.id != neighbourSlot.id) {
              dispatch(fetchTimeslotById(neighbourSlot.id))
            }
          })
        })
  }

  const onMoveUp = () => {
    moveSlotToPosition(Number(slot.position) - 1)
  }

  const onMoveDown = () => {
    moveSlotToPosition(Number(slot.position) + 1)
  }

  const moveSlotToPosition = (newPosition: number) => {
    dispatch(moveSlot({ id: slotId, newPosition }))
      .then(unwrapResult)
      .catch((e) => console.error(e))
  }

  const JingleSelect = () => {
    const jingleOptions = jingles
      .filter((jingle) =>
        station.radioPreset.availableJingles.includes(jingle.id)
      )
      .map((jingle) => ({
        value: jingle.id,
        label: jingle.name,
      }))
    return (
      <ReactSelect
        defaultValue={
          slotJingle?.id
            ? jingleOptions.find((option) => option.value == slotJingle?.id)
            : null
        }
        className="w-100 mb-2"
        placeholder={'Vyberte jingle'}
        options={jingleOptions}
        isSearchable={true}
        isClearable={true}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) => setSelectedJingleId(option ? option.value : null)}
      />
    )
  }
  const NewsSelect = () => {
    const newsOptions = newss
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .map((news) => ({
        value: news.id,
        label: news.visualId,
      }))
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ flexBasis: '100%' }}
      >
        <ReactSelect
          defaultValue={null}
          placeholder={'Přiřaďte zprávu'}
          options={newsOptions}
          isSearchable={true}
          styles={reactSelectDarkmode(theme)}
          onChange={(option) => onSelectNews(option ? option.value : null)}
        />
      </div>
    )
  }

  const DeleteSlotButton = () => (
    <span>
      <Button variant={'danger'} onClick={(e) => onRemoveSlot(e)}>
        Smazat slot
      </Button>
    </span>
  )

  const NewNewsButton = () => (
    <Button variant="primary" onClick={onAddNews}>
      Vytvořit zprávu
    </Button>
  )

  const MovePositionButtons = () => (
    <InputGroup className="d-flex flex-row flex-nowrap rounded-0 w-auto">
      {numberOfSlots > 1 && Number(slot.position) > 0 && (
        <Button
          onClick={onMoveUp}
          variant="outline-secondary"
          size={'sm'}
          className="p-0 w-auto"
        >
          <BsArrowUp />
        </Button>
      )}
      {numberOfSlots > 1 && Number(slot.position) + 1 < numberOfSlots && (
        <Button
          onClick={onMoveDown}
          variant="outline-secondary"
          size={'sm'}
          className="p-0"
        >
          <BsArrowDown />
        </Button>
      )}
    </InputGroup>
  )

  const NewsCard = () => (
    <Card className="bg-light-subtle my-1 rounded-3" style={{height: '210px' }}>
      <div
        className={` m-1 my-2 ps-2 border-start border-3 border-${getStateColor(news ? news?.state : 0)}`}
      >
        <div className="d-flex flex-column justify-content-between">
          <div className="d-flex mb-2 d-flex gap-1">
            <div className=" align-items-center d-flex">
              <span
                className={`bg-secondary rounded-pill px-2 fw-bold text-light`}
              >
                {slot.position + 1}
              </span>
            </div>
            <MovePositionButtons></MovePositionButtons>
            {news && (
              <>
                <span
                  className={`d-flex fw-bold text-${getStateColor(news ? news?.state : 0)} align-items-center me-auto`}
                >
                  {news.visualId}
                </span>
                <Button
                  size={'sm'}
                  className={`bg-${getStateColor(news ? news?.state : 0)} border-0 d-flex align-items-center`}
                  style={{ cursor: 'default' }}
                >
                  <span className={`fw-bold text-light cursor-na`}>
                    {getNewsStateTranslation(news ? news?.state : 0)}
                  </span>
                </Button>
                <AudioPlayButtonComponent
                  title={'Přehrát zprávu'}
                  audioId={news.audioId}
                  showSlider={false}
                ></AudioPlayButtonComponent>
                <Button
                  onClick={onRemoveNews}
                  size={'sm'}
                  variant="danger"
                  className="border-0 d-flex align-items-center"
                >
                  <BsX></BsX>
                </Button>
              </>
            )}
            <Button
              onClick={() => setOpen(!open)}
              aria-controls="collapse-text"
              aria-expanded={open}
              variant={`outline-secondary ${!news && 'ms-auto'}`}
              size={'sm'}
            >
              {open ? <BsEyeSlashFill /> : <BsEyeFill />}
            </Button>
          </div>
          <Collapse in={open}>
            <div
              onClick={news ? onEditNews : () => null}
              style={{ cursor: news ? 'pointer' : 'unset' }}
            >
              {news && <NewsViewComponent news={news} minHeight={140}></NewsViewComponent>}
              {!news && (
                <div
                  style={{ height: '150px' }}
                  className={
                    'd-flex flex-column align-items-center justify-content-center'
                  }
                >
                  {!isDefaultArea() && (
                    <p>Bude použita zpráva z hlavního okruhu</p>
                  )}
                  <div
                    className={
                      'd-flex flex-wrap align-items-center gap-1 justify-content-center'
                    }
                  >
                    <NewsSelect></NewsSelect>
                    <NewNewsButton></NewNewsButton>
                    <DeleteSlotButton></DeleteSlotButton>
                  </div>
                </div>
              )}
            </div>
          </Collapse>
        </div>
        <p className="mt-2 fw-bold mb-1">{news?.title}</p>
      </div>
    </Card>
  )
  return (
    <>
      <div>
        <NewsCard></NewsCard>
        <JingleSelect></JingleSelect>
      </div>
      {showModal && (
        <NewsEditorComponent
          newsToEditId={news ? news.id : undefined}
          show={showModal}
          onHide={(asyncPromise) => onHideEditor(asyncPromise)}
          slot={slot}
        ></NewsEditorComponent>
      )}
    </>
  )
}

export default SlotComponent
